// 11/02 (Josh): feature-based access. Mirrors backend/utils/features.py — the API returns the EFFECTIVE feature set
// on every user payload (`effective_features`, `effective_scope`); this module only reads it.
const RANK = { off: 0, ro: 1, on: 2, rw: 2 };

export const isSuperAdmin = (user) => !!user?.roles?.includes('super_admin');

/** Level the user holds for a feature: 'off' | 'ro' | 'rw' | 'on'. */
export const featureLevel = (user, key) => {
  if (isSuperAdmin(user)) return 'rw';
  return user?.effective_features?.[key] || 'off';
};

/** True when the user's level for `key` is at least `level` ('ro' | 'rw' | 'on'). */
export const hasFeature = (user, key, level = 'ro') => {
  if (!user || !key) return false;
  if (isSuperAdmin(user)) return true;
  return (RANK[featureLevel(user, key)] || 0) >= (RANK[level] || 0);
};

/** A legacy admin (role, no explicit feature set) keeps every page as before. */
export const isLegacyAdmin = (user) =>
  !!user?.roles?.includes('admin') && !(user?.features && Object.keys(user.features).length);

// Page path prefix → feature (longest prefix wins). Pages not listed keep their old role rule.
export const PAGE_FEATURES = [
  ['/deals', 'deals'], ['/refi-chains', 'refi_chains'], ['/contacts', 'contacts'],
  ['/funding-confirmations', 'funding_confirmations'],
  ['/portal', 'investor_portal'], ['/syndication-dashboard', 'syndications'], ['/syndications/payouts', 'payouts'],
  ['/syndications/payables', 'syndications'], ['/syndications', 'syndications'],
  ['/syndication-withdrawals', 'withdrawals'], ['/withdrawal-requests', 'withdrawals_approve'],
  ['/syndications/disbursements', 'withdrawals_approve'], ['/syndications/money-out', 'payouts'],
  ['/commissions', 'commissions'], ['/partner-ledger', 'partner_ledger'],
  ['/daily-import-review', 'daily_import'], ['/uploads', 'imports'], ['/bulk-import', 'imports'],
  ['/sheet-import', 'imports'], ['/mca-import', 'imports'],
  ['/collections', 'collections'], ['/inbox', 'inbox'], ['/legal', 'legal'],
  ['/reports', 'reports'], ['/portfolio', 'reports'],
  ['/submit-application', 'applications'], ['/my-applications', 'applications'], ['/applications', 'applications'],
  ['/uw-toolkit', 'documents_generate'], ['/uw-admin', 'documents_generate'], ['/contracts', 'documents_generate'],
  ['/users', 'users'], ['/data-health', 'system_admin'], ['/admin/jobs', 'system_admin'],
  ['/mila-memory', 'mila'],
].sort((a, b) => b[0].length - a[0].length);

export const pageFeature = (pathname) => {
  const hit = PAGE_FEATURES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return hit ? hit[1] : null;
};

/** Route guard rule: null = not feature-mapped (caller falls back to roles). */
export const pageAllowed = (user, pathname) => {
  const key = pageFeature(pathname);
  if (!key) return null;
  return hasFeature(user, key, 'ro');
};
